"use client";
import ProductPage from "@/components/ProductPage";
import React, { useState } from "react";
import {
  Check,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
  X,
  Send,
  Briefcase,
  Users,
  Code2,
  Monitor,
  Headset,
  GraduationCap,
  Loader2,
  Paperclip,
} from "lucide-react";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";

import ImageHero from "./hero.png";

// ─── DATA ─────────────────────────────────────────────────────────────────────

const JOBS = [
  {
    id: 1,
    icon: Code2,
    title: "Softwareentwickler",
    department: "Entwicklung",
    location: "Eschborn / Remote",
    type: "Vollzeit",
    level: "Junior – Senior",
    description:
      "Du entwickelst moderne Anwendungen mit aktuellen Technologien (z. B. .NET, React, Angular) und arbeitest eng mit unserem agilen Team zusammen. Von der Konzeption bis zum Deployment bist du mittendrin.",
    tasks: [
      "Entwicklung und Pflege von Softwarelösungen in .NET und JavaScript-Frameworks",
      "Code-Reviews und aktive Mitgestaltung unserer technischen Architektur",
      "Enge Zusammenarbeit mit Projektmanagement und Kunden",
      "Dokumentation und Qualitätssicherung",
    ],
    requirements: [
      "Abgeschlossenes Studium der Informatik oder vergleichbare Ausbildung",
      "Erfahrung mit C#/.NET oder modernen JavaScript-Frameworks",
      "Teamgeist und Eigeninitiative",
      "Gute Deutsch- und Englischkenntnisse",
    ],
    benefits: ["Homeoffice möglich", "Moderne Tools", "Flexible Zeiten"],
  },
  {
    id: 2,
    icon: Monitor,
    title: "Webentwickler",
    department: "Web & Design",
    location: "Eschborn / Remote",
    type: "Vollzeit / Teilzeit",
    level: "Junior – Mid",
    description:
      "Du gestaltest und entwickelst moderne Webseiten und Webanwendungen für unsere Kunden. Dabei hast du sowohl Frontend als auch Performance und Barrierefreiheit im Blick.",
    tasks: [
      "Entwicklung responsiver Webanwendungen mit React / Next.js",
      "Umsetzung von UI/UX-Konzepten in sauberen, wartbaren Code",
      "Performance-Optimierung und SEO-technische Umsetzung",
      "Abstimmung mit Designern und Backend-Entwicklern",
    ],
    requirements: [
      "Fundierte Kenntnisse in HTML, CSS und JavaScript / TypeScript",
      "Erfahrung mit React oder vergleichbaren Frameworks",
      "Sinn für gutes Design und Usability",
      "Eigenverantwortliche Arbeitsweise",
    ],
    benefits: [
      "Homeoffice möglich",
      "Moderne Tools",
      "Spannende Kundenprojekte",
    ],
  },
  {
    id: 3,
    icon: Headset,
    title: "IT-Support & Systembetreuung",
    department: "IT-Operations",
    location: "Eschborn",
    type: "Vollzeit",
    level: "Berufseinsteiger – Erfahren",
    description:
      "Du bist erste Anlaufstelle für unsere Kunden bei technischen Fragen und sorgst für einen reibungslosen Betrieb ihrer IT-Systeme.",
    tasks: [
      "1st- und 2nd-Level-Support für Kundenanfragen",
      "Einrichtung und Wartung von Arbeitsplätzen und Netzwerken",
      "Dokumentation von Störungen und Lösungen im Ticketsystem",
      "Koordination mit externen Dienstleistern",
    ],
    requirements: [
      "Ausbildung als Fachinformatiker oder vergleichbar",
      "Kenntnisse in Windows-Systemumgebungen",
      "Serviceorientierung und Kommunikationsstärke",
      "Führerschein Klasse B von Vorteil",
    ],
    benefits: ["Geregelte Arbeitszeiten", "Moderne Tools", "Teamevents"],
  },
  {
    id: 5,
    icon: GraduationCap,
    title: "Ausbildung & Praktikum",
    department: "Ausbildung",
    location: "Eschborn",
    type: "Ausbildung / Praktikum",
    level: "Einsteiger",
    description:
      "Wir bieten Ausbildungsplätze als Fachinformatiker sowie Praktika für motivierte Nachwuchskräfte.",
    tasks: [
      "Einblick in alle Bereiche der Softwareentwicklung und des IT-Supports",
      "Mitarbeit an echten Kundenprojekten von Anfang an",
      "Begleitung durch erfahrene Mentor:innen",
      "Regelmäßige Feedbackgespräche und Entwicklungsziele",
    ],
    requirements: [
      "Interesse an IT und Technologie",
      "Lernbereitschaft und Zuverlässigkeit",
      "Guter Schulabschluss (mind. mittlere Reife)",
      "Teamfähigkeit",
    ],
    benefits: ["Mentoring-Programm", "Übernahmechancen", "Azubi-Events"],
  },
];

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium bg-[#e8edf5] text-brand-primary rounded-full px-3 py-1">
      {children}
    </span>
  );
}

function ApplicationModal({
  job,
  onClose,
}: {
  job: (typeof JOBS)[0];
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    files: [] as File[],
  });
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = Array.from(e.target.files ?? []);
    setForm((f) => ({ ...f, files: [...f.files, ...selectedFiles] }));
  }

  function removeFile(index: number) {
    setForm((f) => ({ ...f, files: f.files.filter((_, i) => i !== index) }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");

    const formData = new FormData();
    formData.append("name", form.name);
    formData.append("email", form.email);
    formData.append("product", `Bewerbung: ${job.title}`);
    formData.append(
      "nachricht",
      `Telefon: ${form.phone}\n\nNachricht:\n${form.message}`,
    );
    formData.append("unternehmen", "Karriereportal");
    form.files.forEach((file) => {
      formData.append("attachments", file); // ✅ richtiger Name
    });

    try {
      const response = await fetch("/api/send", {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Fehler:", error);
      setStatus("error");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-brand-primary text-white rounded-t-2xl px-6 py-5 flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-blue-200 mb-1">
              Bewerbung für
            </p>
            <h2 className="text-xl font-bold leading-tight">{job.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 ml-4 mt-1 text-blue-200 hover:text-white transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        {status === "success" ? (
          <div className="px-6 py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-600" size={28} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">
              Bewerbung gesendet!
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Vielen Dank! Wir haben Ihre Nachricht erhalten und melden uns in
              Kürze bei Ihnen.
            </p>
            <button
              onClick={onClose}
              className="mt-6 px-6 py-2.5 rounded-full bg-brand-primary text-white font-medium text-sm hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl transition-colors"
            >
              Schließen
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
            <p className="text-sm text-slate-500">
              Füllen Sie das Formular aus und laden Sie ggf. Ihren Lebenslauf
              hoch.
            </p>

            {status === "error" && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg">
                Fehler beim Senden. Bitte versuchen Sie es später erneut.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name *
                </label>
                <input
                  required
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Max Mustermann"
                  className="w-full border border-slate-200 rounded-full px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  E-Mail *
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="max@beispiel.de"
                  className="w-full border border-slate-200 rounded-full px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Telefonnummer
              </label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+49 (0) 6196 - 950 591 0"
                className="w-full border border-slate-200 rounded-full px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nachricht *
              </label>
              <textarea
                required
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={3}
                placeholder="Ich bewerbe mich, weil..."
                className="w-full border border-slate-200 rounded-full px-4 py-2.5 text-sm text-black focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Anhänge (Lebenslauf, Zeugnisse)
              </label>
              <div className="mt-1 flex flex-col gap-2">
                <label className="cursor-pointer flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-200 hover:border-brand-primary hover:bg-slate-50 rounded-full px-4 py-4 transition-all group">
                  <Paperclip
                    size={18}
                    className="text-slate-400 group-hover:text-brand-primary"
                  />
                  <span className="text-sm text-slate-500 group-hover:text-brand-primary">
                    Dateien auswählen
                  </span>
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.png"
                  />
                </label>

                {form.files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.files.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 bg-blue-50 border border-blue-100 px-3 py-1.5 rounded-lg"
                      >
                        <span className="text-xs font-medium text-brand-primary truncate max-w-150">
                          {file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={status === "sending"}
              className="w-full flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl disabled:bg-slate-300 text-white font-semibold py-3 rounded-full transition-colors text-sm"
            >
              {status === "sending" ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Wird
                  gesendet...
                </>
              ) : (
                <>
                  <Send size={16} /> Bewerbung jetzt senden
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function JobCard({ job }: { job: (typeof JOBS)[0] }) {
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const Icon = job.icon;

  return (
    <>
      {modalOpen && (
        <ApplicationModal job={job} onClose={() => setModalOpen(false)} />
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-12 h-12 rounded-full bg-[#e8edf5] flex items-center justify-center">
              <Icon className="text-brand-primary" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-slate-900 leading-snug">
                {job.title}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">{job.department}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Badge>
              <MapPin size={12} />
              {job.location}
            </Badge>
            <Badge>
              <Clock size={12} />
              {job.type}
            </Badge>
            <Badge>
              <Users size={12} />
              {job.level}
            </Badge>
          </div>

          <p className="mt-4 text-slate-600 text-sm leading-relaxed">
            {job.description}
          </p>
        </div>

        {expanded && (
          <div className="px-6 pb-4 border-t border-slate-100 pt-4 space-y-5">
            <div>
              <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2">
                <Briefcase size={14} className="text-brand-primary" /> Deine
                Aufgaben
              </h4>
              <ul className="space-y-1.5">
                {job.tasks.map((t, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-slate-600"
                  >
                    <Check
                      className="shrink-0 mt-0.5 text-brand-primary"
                      size={14}
                    />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-800 mb-2">
                Was du mitbringst
              </h4>
              <ul className="space-y-1.5">
                {job.requirements.map((r, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-slate-600"
                  >
                    <Check
                      className="shrink-0 mt-0.5 text-brand-primary"
                      size={14}
                    />
                    {r}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-800 mb-2">
                Das bieten wir
              </h4>
              <div className="flex flex-wrap gap-2">
                {job.benefits.map((b, i) => (
                  <span
                    key={i}
                    className="text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-3 py-1 font-medium"
                  >
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <button
            onClick={() => setExpanded((e) => !e)}
            className="flex items-center gap-1 text-sm font-medium text-brand-primary hover:text-brand-secondary transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp size={16} /> Weniger anzeigen
              </>
            ) : (
              <>
                <ChevronDown size={16} /> Details anzeigen
              </>
            )}
          </button>

          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 bg-brand-primary hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
          >
            <Send size={14} /> Jetzt bewerben
          </button>
        </div>
      </div>
    </>
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────

export default function Karriere() {
  const [activeFilter, setActiveFilter] = useState("Alle");
  const departments = [
    "Alle",
    ...Array.from(new Set(JOBS.map((j) => j.department))),
  ];
  const filtered =
    activeFilter === "Alle"
      ? JOBS
      : JOBS.filter((j) => j.department === activeFilter);

  return (
    <ProductPage
      productName="Karriere bei SDI Group"
      hero={{
        title: "Werde Teil unseres Teams",
        subtitle:
          "Wir suchen talentierte Menschen, die gemeinsam mit uns innovative Softwarelösungen entwickeln und Unternehmen voranbringen.",
        buttonLeft: {
          title: "Stellenangebote ansehen",
          href: "#stellen",
        },
        buttonRight: {
          title: "Initiativbewerbung",
          href: "mailto:info@sdigroup.de",
        },
        image: ImageHero,
        imageAlt: "Karriere bei SDI Group",
      }}
      highlight={{
        image: ImageHero,
        bullets: [
          <>
            <strong>Flache Hierarchien & offene Unternehmenskultur</strong>
          </>,
          <>
            <strong>Spannende Projekte für namhafte Kunden</strong>
          </>,
          <>
            <strong>Flexible Arbeitszeiten & Homeoffice-Möglichkeiten</strong>
          </>,
          <>
            <strong>Kontinuierliche Weiterbildung & Schulungen</strong>
          </>,
          <>
            <strong>Moderner Arbeitsplatz & top Ausstattung</strong>
          </>,
          <>
            <strong>Langfristige Perspektiven & Karrierechancen</strong>
          </>,
        ],
      }}
      beforeVideo={
        <>
          {/* IMAGETEXT BLOCK */}
          <Section className="bg-background text-foreground py-12 md:py-20">
            <ImageText
              imagePosition="right"
              imageSrc="/AGesucht.jpg"
              imageAlt="Karriere bei SDI Group"
              title="Deine Karriere bei SDI Group – Gemeinsam wachsen"
            >
              <p className="text-slate-700 leading-relaxed">
                Mit über 25 Jahren Erfahrung in der Softwareentwicklung bieten
                wir ein modernes Arbeitsumfeld, spannende Projekte und echte
                Entwicklungsmöglichkeiten – für Berufseinsteiger und erfahrene
                Fachkräfte gleichermaßen.
              </p>
              <p className="mt-4 text-slate-700 leading-relaxed">
                Bei SDI Group arbeitest du an innovativen Lösungen für namhafte
                Kunden, in einem Team das auf Vertrauen, Offenheit und
                gegenseitige Unterstützung setzt.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Moderne Arbeitskultur:</strong> Flache Hierarchien
                    und offene Kommunikation auf Augenhöhe.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Flexibles Arbeiten:</strong>{" "}
                    Homeoffice-Möglichkeiten und flexible Arbeitszeiten.
                  </span>
                </li>
                <li className="flex items-start gap-3 text-slate-700">
                  <Check
                    className="shrink-0 mt-1 text-brand-primary"
                    size={18}
                  />
                  <span>
                    <strong>Deine Entwicklung:</strong> Weiterbildungen,
                    Zertifizierungen und Karrierechancen inklusive.
                  </span>
                </li>
              </ul>
            </ImageText>
          </Section>

          {/* JOB LISTINGS */}
          <Section id="stellen" className="bg-slate-50 py-16 md:py-24">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-slate-900">
                Aktuelle Stellenangebote
              </h2>
              <p className="text-slate-500 mt-2">
                Finde die passende Stelle und bewirb dich direkt online.
              </p>
            </div>

            {/* Filter */}
            <div className="flex flex-wrap gap-2 mb-10">
              {departments.map((d) => (
                <button
                  key={d}
                  onClick={() => setActiveFilter(d)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    activeFilter === d
                      ? "bg-brand-primary text-white border-brand-primary"
                      : "bg-white text-slate-600 border-slate-200 hover:border-brand-primary"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>

            {/* Job Cards */}
            <div className="grid gap-6 md:grid-cols-2">
              {filtered.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>

            {/* Initiativbewerbung Banner */}
            <div className="mt-14 rounded-2xl bg-brand-primary text-white p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Kein passendes Angebot dabei?
              </h2>
              <p className="text-blue-200 max-w-xl mx-auto leading-relaxed mb-6">
                Wir sind immer an talentierten Kolleg:innen interessiert.
                Schreiben Sie uns einfach an.
              </p>
              <a
                href="mailto:info@sdigroup.de"
                className="inline-flex items-center gap-2 bg-white text-brand-primary font-bold px-7 py-3.5 rounded-full hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl transition-colors"
              >
                <Send size={16} /> Initiativ bewerben
              </a>
            </div>
          </Section>

          {/* PROZESS */}
          <section className="bg-white py-16 md:py-24 px-6">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-2 text-center">
                So läuft Ihre Bewerbung ab
              </h2>
              <p className="text-slate-500 text-center mb-12">
                Unkompliziert, schnell und auf Augenhöhe.
              </p>
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  {
                    step: "01",
                    title: "Bewerbung einreichen",
                    desc: "Füllen Sie das Formular aus und senden Sie uns Ihre Nachricht direkt zu.",
                  },
                  {
                    step: "02",
                    title: "Eingangsbestätigung",
                    desc: "Wir bestätigen den Eingang Ihrer Bewerbung innerhalb von 2 Werktagen.",
                  },
                  {
                    step: "03",
                    title: "Kennenlerngespräch",
                    desc: "Ein erstes, lockeres Gespräch – telefonisch oder per Videocall.",
                  },
                  {
                    step: "04",
                    title: "Fachliches Interview",
                    desc: "Wir tauchen gemeinsam tiefer in die Aufgaben und Ihre Erfahrungen ein.",
                  },
                ].map((p) => (
                  <div
                    key={p.step}
                    className="bg-slate-50 rounded-2xl p-6 border border-slate-200 shadow-sm"
                  >
                    <span className="text-4xl font-black text-brand-primary/20">
                      {p.step}
                    </span>
                    <h3 className="text-base font-bold text-slate-800 mt-2 mb-1">
                      {p.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed">
                      {p.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      }
      faqs={[
        {
          q: "Wie läuft der Bewerbungsprozess ab?",
          a: "Nach Eingang Ihrer Bewerbung melden wir uns innerhalb weniger Tage. Es folgt ein erstes Kennenlerngespräch, danach ein fachliches Interview – unkompliziert und auf Augenhöhe.",
        },
        {
          q: "Sind auch Initiativbewerbungen möglich?",
          a: "Ja. Auch wenn gerade keine passende Stelle ausgeschrieben ist, freuen wir uns über Initiativbewerbungen – wir sind immer an talentierten Kolleg:innen interessiert.",
        },
        {
          q: "Bieten Sie Homeoffice an?",
          a: "Ja. Wir bieten flexible Arbeitsmodelle mit der Möglichkeit zum Homeoffice – abgestimmt auf die jeweilige Rolle und die persönlichen Bedürfnisse.",
        },
        {
          q: "Welche Weiterbildungsmöglichkeiten gibt es?",
          a: "Wir unterstützen aktiv Weiterbildungen, Zertifizierungen und den Besuch von Fachkonferenzen – Ihre Entwicklung liegt uns am Herzen.",
        },
        {
          q: "Für welche Standorte suchen Sie Mitarbeiter?",
          a: "Unsere Stellen sind an unserem Hauptstandort sowie teilweise remote verfügbar. Details entnehmen Sie bitte der jeweiligen Stellenausschreibung.",
        },
        {
          q: "Was sollte ich in meiner Bewerbung mitschicken?",
          a: "Lebenslauf, kurzes Anschreiben und – falls vorhanden – Arbeitsproben oder Zeugnisse. Alles einfach per E-Mail oder direkt über das Bewerbungsformular.",
        },
      ]}
      features={[]}
    />
  );
}
